class MiniCart extends HTMLElement {
  constructor() {
    super();

    this.drawer = document.querySelector('cart-drawer');
    this.freeGiftVariantId = 49737346777325; // Your gift variant ID
    this.freeGiftThreshold = 125000;         // $1000.00 in cents
    this._giftHandling = false;

    new IntersectionObserver(this.handleIntersection.bind(this)).observe(this);
  }

  // Called once by Shopify after the custom element is added to the DOM
  connectedCallback() {
    // Hook fetch HERE (in connectedCallback, not constructor) so it runs
    // after all other scripts have loaded and window.fetch is fully ready
    this._interceptFetch();
  }

  // ─── Intercept window.fetch ───────────────────────────────────────────────
  // We wrap window.fetch so that after ANY cart mutation call completes
  // (cart/change, cart/update, cart/remove, cart/clear), we automatically
  // re-check whether the free gift should be added or removed.
  //
  // KEY FIX vs previous version: we await the response.text() clone to fully
  // resolve before triggering handleFreeGift — this ensures Shopify has
  // finished processing the cart change before we query /cart.js
  _interceptFetch() {
    // Avoid double-patching if connectedCallback fires more than once
    if (window._fetchGiftPatched) return;
    window._fetchGiftPatched = true;

    const originalFetch = window.fetch;
    const self = this;

    window.fetch = async function(...args) {
      const response = await originalFetch.apply(this, args);

      const url = (typeof args[0] === 'string' ? args[0] : args[0]?.url) || '';

      const isCartMutation = (
        url.includes('/cart/change') ||
        url.includes('/cart/update') ||
        url.includes('/cart/remove') ||
        url.includes('/cart/clear')
      );

      // Skip our own gift-related calls to avoid infinite loops
      const isGiftCall = url.includes('/cart/add') && self._giftHandling;

      if (isCartMutation && !isGiftCall) {
        // Clone the response (the original is still usable by the theme)
        // Wait for the clone to resolve so Shopify has finished its cart update
        response.clone().text().then(() => {
          // Extra 300ms buffer for any synchronous DOM work the theme does
          setTimeout(() => {
            console.log('[FreeGift] Cart mutation detected, checking gift...');
            self.handleFreeGift();
          }, 300);
        });
      }

      return response;
    };

    console.log('[FreeGift] fetch interceptor installed ✅');
  }

  // ─── Intersection / Lazy Load ─────────────────────────────────────────────
  handleIntersection(entries, observer) {
    if (!entries[0].isIntersecting) return;
    observer.unobserve(this);

    fetch(this.dataset.url)
      .then(response => response.text())
      .then(html => {
        document.getElementById('mini-cart').innerHTML =
          this.getSectionInnerHTML(html, '.shopify-section');
      })
      .catch(e => console.error(e));
  }

  // ─── Open Drawer ──────────────────────────────────────────────────────────
  open() {
    const detailsElement = this.drawer.querySelector('details');
    if (detailsElement.hasAttribute('open')) return;
    this.drawer.querySelector('summary').click();
    setTimeout(() => setHeaderHeight(), 250);
  }

  // ─── Called when a product is added to cart ───────────────────────────────
  async renderContents(parsedState) {
    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      document.getElementById(section.id).innerHTML =
        this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    await this.handleFreeGift();

    if (this.header) this.header.reveal();
    this.open();
  }

  // ─── Core Gift Logic ──────────────────────────────────────────────────────
  async handleFreeGift() {
    if (this._giftHandling) return;
    this._giftHandling = true;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      let giftLineIndex = null;
      let giftInCart = null;
      cart.items.forEach((item, index) => {
        if (item.variant_id === this.freeGiftVariantId) {
          giftInCart = item;
          giftLineIndex = index + 1; // Shopify line index is 1-based
        }
      });

      // const cartTotal = cart.items.reduce((total, item) => {
      //   if (item.variant_id === this.freeGiftVariantId) return total;
      //   return total + (Number(item.final_line_price) || 0);
      // }, 0);

      let cartTotal = cart.total_price;

      // Remove gift value if already in cart
      if (giftInCart) {
        cartTotal -= giftInCart.final_line_price;
      }

      console.log(
        '[FreeGift] Cart total (cents):', cartTotal,
        '| Threshold:', this.freeGiftThreshold,
        '| Gift in cart:', !!giftInCart,
        '| Line index:', giftLineIndex
      );

      if (cartTotal >= this.freeGiftThreshold && !giftInCart) {
        await this.addFreeGift();
      } else if (cartTotal < this.freeGiftThreshold && giftInCart) {
        await this.removeFreeGift(giftLineIndex);
      }

    } catch (e) {
      console.error('[FreeGift] Error in handleFreeGift:', e);
    } finally {
      this._giftHandling = false;
    }
  }

  // ─── Add Gift ─────────────────────────────────────────────────────────────
  async addFreeGift() {
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{
            id: this.freeGiftVariantId,
            quantity: 1,
            properties: {
              _is_free_gift: true,
              'Free Gift': '1'
            }
          }]
        })
      });

      if (!res.ok) throw new Error('Failed to add: ' + await res.text());

      console.log('[FreeGift] Gift added ✅');
      await this.refreshCart();

    } catch (e) {
      console.error('[FreeGift] Error adding gift:', e);
    }
  }

  // ─── Remove Gift ──────────────────────────────────────────────────────────
  async removeFreeGift(lineIndex) {
    console.log('[FreeGift] Removing gift at line:', lineIndex);

    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line: lineIndex, quantity: 0 })
      });

      const data = await res.json();
      console.log('[FreeGift] Remove response:', data);

      if (!res.ok) throw new Error('Remove failed');

      console.log('[FreeGift] Gift removed ✅');
      await this.refreshCart();

    } catch (e) {
      console.error('[FreeGift] line remove failed, trying variant_id fallback:', e);

      try {
        const res = await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates: { [this.freeGiftVariantId]: 0 } })
        });
        const data = await res.json();
        console.log('[FreeGift] Fallback remove response:', data);
        await this.refreshCart();
      } catch (err) {
        console.error('[FreeGift] Fallback also failed:', err);
      }
    }
  }

  // ─── Refresh Cart UI (no page reload) ─────────────────────────────────────
  async refreshCart() {
    try {
      const sectionIds = this.getSectionsToRender().map(s => s.section).join(',');
      const res = await fetch(`/?sections=${sectionIds}`);
      const data = await res.json();

      this.getSectionsToRender().forEach((section) => {
        const el = document.getElementById(section.id);
        if (el && data[section.section]) {
          el.innerHTML = this.getSectionInnerHTML(data[section.section], section.selector);
        }
      });

    } catch (e) {
      console.error('[FreeGift] Error refreshing cart:', e);
    }
  }

  // ─── Helpers ──────────────────────────────────────────────────────────────
  getSectionsToRender() {
    return [
      { id: 'mini-cart', section: 'mini-cart', selector: '.shopify-section' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' }
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }
}

customElements.define('mini-cart', MiniCart);